import { useMemo } from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import VehicleStatement from './VehicleStatement';
import { amountToWords, getPreviousMonthRangeMoment, isValidObject, parseNumber } from '../../../utils';
import { isInovize } from '../../../constants';

const styles = StyleSheet.create({
    page: { padding: 30, fontSize: 10 },
    tableRow: { flexDirection: 'row' },
    leftCell: {
        border: '1pt solid black',
        flexBasis: '50%',
        padding: 0,
    },
    rightCell: {
        border: '1pt solid black',
        flexBasis: '50%',
        padding: 0,
    },
    leftInnerRow: {
        borderBottom: '1pt solid black',
        padding: 5,
        height: 110,
        justifyContent: 'center',
    },
    rightSplitRow: {
        flexDirection: 'row',
        borderBottom: '1pt solid black',
        height: 25,
    },
    rightSplitCell: {
        flex: 1,
        borderRight: '1pt solid black',
        justifyContent: 'center',
        padding: 5,
    },
    rightSplitCellLast: {
        flex: 1,
        justifyContent: 'center',
        padding: 5,
    },
    // New table styles
    secondTable: {
        border: '1pt solid black',
    },
    secondHeader: {
        flexDirection: 'row',
        backgroundColor: '#f0f0f0',
        fontWeight: 'bold',
        borderBottom: '1pt solid black',
    },
    secondHeaderCell: {
        padding: 6,
        flex: 1,
        borderRight: '1pt solid black',
        justifyContent: 'center',
        textAlign: "center"
    },
    secondRow: {
        flexDirection: 'row',
    },
    secondCell: {
        padding: 6,
        borderRight: '1pt solid black',
        flex: 1,
        justifyContent: 'center',
    },
    lastCell: {
        borderRight: 'none',
    },
    wid10: {
        flex: 5
    },
    eSignContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    eSignImage: {
        alignSelf: 'center',
        justifySelf: 'flex-start',
        objectFit: 'contain',
    },
});

const termStyle = {
  container: {
    flexDirection: 'column',
    padding: 5,
  },
  itemRow: {
    flexDirection: 'row',
    marginTop: 5,
  },
  text: {
    flex: 1,
  },
};


const getFormattedData = (list, isRenewal, isComboCase, isQuotation) => {
    if (list?.length > 0) {
        const sum = list.reduce((acc, curr) => acc += Number(curr?.billingamount || curr?.amount || "0"), 0);
        const result = [isComboCase ? "Combo Monthly Of Vehicle Tracking Device" : isRenewal ? "Renewal of Assembled tracking devices" : "Assembled vehicle tracking devices", isQuotation ? list?.[0]?.quantity ?? 1 : list?.length ?? 1, `${list?.[0]?.billingamount || list?.[0]?.amount || 0} Pc`, sum ?? 0];
        return result;
    }
    return null;
}

const TableWithSplitAndMerged = ({ data, firstInvoice, isQuotation = false }) => {
    const dealerId = localStorage.getItem("dealerId") || 0;
    // const amountToWords = useAmountToWords(12345);
    const leftRows = useMemo(() => {
        const fromData = data?.fromCompany ?? {};
        const toData = data?.toCompany ?? {};
        return [{type: "From,", ...fromData}, {type: "To,", ...toData}]
    }, [data?.fromCompany, data?.toCompany]);

    const rightRows = useMemo(() => [
        ["Invoice No.", "Date:-"],
        [data?.invoiceNo ?? "", data?.date ?? ""],
        ["Suppliers / Ref", "Mode / Terms of Payment"],
        ["", ""],
        ["Buyer Order No.", "Dated"],
        ["", ""],
    ], [data?.invoiceNo]);

    const secondTableData = useMemo(() => {
        const tableData = [];
        const groupedByAmount = {};
        if(data?.vehicles?.length > 0){
            data?.vehicles?.forEach((item) => {
                if(isQuotation) {
                    const descList = getFormattedData(item, item?.is_renewal, data?.isComboCase, true) ?? [];
                    if(data?.gstPercentage) descList.splice(1, 0, item?.is_renewal ? data?.fromCompany?.sacCode : data?.fromCompany?.hsnCode);
                    descList[0] = item?.[0]?.description ?? "";
                    descList[descList.length - 1] = parseNumber(item?.[0]?.amount ?? 0) * parseNumber(item?.[0]?.quantity ?? 0);
                    tableData.push([(tableData?.length ?? 0) + 1, ...descList]);
                } else {
                    const amountKey = `${item?.amount ?? 0}_${item?.is_renewal ? "Renew": "New"}`;
                    if (!groupedByAmount[amountKey]) groupedByAmount[amountKey] = [];
                    groupedByAmount[amountKey].push(item);
                }
            })
        }
        if(!isQuotation && isValidObject(groupedByAmount)) {
            const invoiceItems = [];
            Object.entries(groupedByAmount).forEach(([key, items]) => {
                const [amount, type] = key.split("_");
                invoiceItems.push([items, type == "Renew"]);
            });
            invoiceItems?.forEach(([list, isRenew]) => {
                if (list?.length > 0) {
                    const descList = getFormattedData(list, isRenew, data?.isComboCase, false) ?? [];
                    if(data?.gstPercentage) descList.splice(1, 0, isRenew ? data?.fromCompany?.sacCode : data?.fromCompany?.hsnCode)
                    tableData.push([(tableData?.length ?? 0) + 1, ...descList]);
                    if(data?.isComboCase) {
                        const noteEntry = ["", getPreviousMonthRangeMoment(data?.date), "", "", ""];
                        if(data?.gstPercentage) noteEntry.splice(2, 0, null);
                        tableData.push(noteEntry);
                    }
                }
            });
        }
        return tableData;
    }, [data?.vehicles]);

    const grandTotal = useMemo(() => Math.floor(parseNumber(data?.amount ?? 0) + (data?.gstPercentage ? parseNumber(data?.gstAmount ?? 0) : 0)), [data?.amount]);
    const totalWords = useMemo(() => amountToWords(grandTotal ?? 0), [grandTotal]);
    const totalFlex = useMemo(() => (data?.gstPercentage) ? 9.95 : 7.75, [data?.gstPercentage]);

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View>
                    <Text style={{ textAlign: 'center', fontSize:"18px", fontWeight: 600, marginBottom: "5px" }}>{isQuotation ? "QUOTATION" : "INVOICE"}</Text>
                </View>
                <View style={styles.tableRow}>
                    {/* Left Column with 2 rows */}
                    <View style={styles.leftCell}>
                        {leftRows.map((text, index) => (
                            <View
                                key={index}
                                style={[
                                    styles.leftInnerRow,
                                    index === leftRows.length - 1 && { borderBottom: 'none' }
                                ]}>
                                <Text style={{ marginBottom: "2px" }}>{text?.type || ""}</Text>
                                <Text style={{ marginBottom: "5px" }}>{text?.name || ""}</Text>
                                <Text style={{ marginBottom: "2px" }}>{text?.address || ""}</Text>
                                {text?.gst ? <Text style={{ marginBottom: "2px" }}>GSTIN: {text?.gst || ""}</Text> : null}
                                {text?.email ? <Text style={{ marginBottom: "2px" }}>Mail: {text?.email || ""}</Text> : null}
                                {text?.phoneNo ? <Text style={{ marginBottom: "2px" }}>Mo: {text?.phoneNo || ""}</Text> : null}
                            </View>
                        ))}
                    </View>

                    {/* Right Column: 6 rows with 2 cells each + 1 merged row */}
                    <View style={styles.rightCell}>
                        {rightRows.map((pair, index) => {
                            return <View
                                key={index}
                                style={[styles.rightSplitRow]}>
                                <View style={styles.rightSplitCell}>
                                    <Text>{pair[0]}</Text>
                                </View>
                                <View style={styles.rightSplitCellLast}>
                                    <Text>{pair[1]}</Text>
                                </View>
                            </View>
                        })}

                        {/* Merged Row */}
                        <View style={termStyle.container}>
                            <Text style={{ textDecoration: 'underline', fontWeight: 600 }}>
                                Terms & Conditions
                            </Text>

                            <View style={termStyle.itemRow}>
                                <Text style={termStyle.text}>
                                    {`1) After 1 year renewal charges would be Rs. ${dealerId == 7 ? 2400 : 1800}/- per vehicle per year for regular GPS`}
                                </Text>
                            </View>

                            {isInovize ? null : <>
                                <View style={termStyle.itemRow}>
                                    <Text style={termStyle.text}>
                                        2) After 1 year renewal charges would be Rs. 4000/- per vehicle per year for Gujarat Mining GPS
                                    </Text>
                                </View>

                                <View style={termStyle.itemRow}>
                                    <Text style={termStyle.text}>
                                        3) After 1 year renewal charges would be Rs. 2800/- per vehicle per year for fuel sensor
                                    </Text>
                                </View>
                            </>}
                        </View>

                    </View>
                </View>
                {/* Second table: invoice-like structure */}
                <View style={[styles.secondTable]}>
                    {/* Header Row */}
                    <View style={styles.secondHeader}>
                        <Text style={[styles.secondHeaderCell]}>Sl. No.</Text>
                        <Text style={[styles.secondHeaderCell, styles.wid10]}>Description</Text>
                        {data?.gstPercentage ? <Text style={[styles.secondHeaderCell, { flex: 2}]}>SAC/HSN</Text> : null}
                        <Text style={[styles.secondHeaderCell]}>Qty</Text>
                        <Text style={[styles.secondHeaderCell]}>Per Unit</Text>
                        <Text style={[styles.secondHeaderCell, { flex: 2 }, styles.lastCell]}>Total</Text>
                    </View>

                    <View>
                        {/* Data Rows */}
                        {secondTableData.map((row, rowIndex) => (
                            <View key={rowIndex} style={[styles.secondRow, rowIndex == secondTableData.length - 1 && { borderBottom: "1px solid black" }]}>
                                {row.map((cell, cellIndex) => (
                                    <Text
                                        key={cellIndex}
                                        style={[
                                            styles.secondHeaderCell,
                                            cellIndex == row.length - 1 && styles.lastCell,
                                            { flex: cellIndex == 1 ? 5 : (cellIndex == row.length - 1 || (data?.gstPercentage && cellIndex == 2) || (cell == null)) ? 2 : 1 }
                                        ]}>
                                        {cell}
                                    </Text>
                                ))}
                            </View>
                        ))}
                        <View style={{ flex: 1 }} />
                    </View>

                    {data?.gstPercentage ? !data?.toCompany?.gst?.startsWith("24") ? <View style={[styles.secondRow, { borderTop: "1px solid black" }]}>
                        <Text style={[styles.secondCell, { flex: totalFlex, padding: 6, textAlign: 'right', borderBottom: "1px solid black" }]}>{`IGST @ ${(data?.gstPercentage ?? 0)}%`}</Text>
                        <Text style={[{ flex: 1.75, padding: 6, textAlign: 'center', borderBottom: "1px solid black" }]}>{(data?.gstAmount ?? 0)}</Text>
                    </View> : <>
                        <View style={[styles.secondRow, { borderTop: "1px solid black" }]}>
                            <Text style={[styles.secondCell, { flex: totalFlex, padding: 6, textAlign: 'right', borderBottom: "1px solid black" }]}>{`CGST @ ${(data?.gstPercentage ?? 0) / 2}%`}</Text>
                            <Text style={[{ flex: 1.75, padding: 6, textAlign: 'center', borderBottom: "1px solid black" }]}>{(data?.gstAmount ?? 1) / 2}</Text>
                        </View>
                        <View style={styles.secondRow}>
                            <Text style={[styles.secondCell, { flex: totalFlex, padding: 6, textAlign: 'right', borderBottom: "1px solid black" }]}>{`SGST @ ${(data?.gstPercentage ?? 0) / 2}%`}</Text>
                            <Text style={[{ flex: 1.75, padding: 6, textAlign: 'center', borderBottom: "1px solid black" }]}>{(data?.gstAmount ?? 1) / 2}</Text>
                        </View>
                    </> : null}
                    <View style={styles.secondRow}>
                        <Text style={[styles.secondCell, { flex: totalFlex, padding: 6, textAlign: 'right', borderBottom: "1px solid black" }]}>Total</Text>
                        <Text style={[{ flex: 1.75, padding: 6, textAlign: 'center', borderBottom: "1px solid black" }]}>{grandTotal}</Text>
                    </View>
                    <View style={styles.secondRow}>
                        <Text style={[styles.secondCell, { flex: totalFlex, padding: 6, textAlign: 'right' }]}>Payable Amount</Text>
                        <Text style={{ flex: 1.75, padding: 6, textAlign: 'center', }}>{grandTotal}</Text>
                    </View>
                </View>

                <View style={styles.tableRow}>
                    {/* Left Column with 2 rows */}
                    <View style={styles.leftCell}>
                        <View style={[
                            styles.leftInnerRow
                        ]}>
                            <Text style={{
                                marginBottom: "10px"
                            }}>Payable Amount in word</Text>
                            <Text style={{
                                marginBottom: "10px",
                                textTransform: "uppercase",
                                fontWeight: "600"
                            }}>INR:- {totalWords} ONLY</Text>
                        </View>
                        <View style={{ padding: "5px 10px" }}>
                            <Text>{"Description \nWe declare that this invoice shows the actual price of the goods described and that all particulars are true and correct."}</Text>
                        </View>
                    </View>

                    <View style={styles.leftCell}>
                        <View style={[
                            styles.leftInnerRow,
                            { padding: "5px" }
                        ]}>
                            <Text style={{ marginBottom: "5px" }}>Company's Bank Details</Text>
                            <Text style={{ marginBottom: "5px" }}>A/c Name :- {data?.fromCompany?.name || ""}</Text>
                            <Text style={{ marginBottom: "2px" }}>Bank Name :- {data?.fromCompany?.bankName || ""}</Text>
                            <Text style={{ marginBottom: "2px" }}>Branch :- {data?.fromCompany?.branch || ""}</Text>
                            <Text style={{ marginBottom: "2px" }}>Current Account No.:- {data?.fromCompany?.accountNo || ""}</Text>
                            <Text style={{ marginBottom: "2px" }}>IFSC Code :- {data?.fromCompany?.ifscCode || ""}</Text>
                        </View>
                        <View style={[
                            styles.leftInnerRow,
                            { padding: "5px", borderBottom: "0px" }
                        ]}>
                            <Text style={{ fontWeight: "600" }}>For, {data?.fromCompany?.name ?? ""}</Text>
                            <View style={styles.eSignContainer}>
                                {data?.fromCompany?.digitalSignatureUrl ? (
                                    <Image style={[styles.eSignImage,  { height: 40 }]} src={data.fromCompany.digitalSignatureUrl} />
                                ) : null}
                                {data?.fromCompany?.stampUrl ? (
                                    <Image style={[styles.eSignImage, { height: 80 }]} src={data.fromCompany.stampUrl} />
                                ) : null}
                            </View>
                            <Text style={{ fontWeight: "600" }}>Authorized Signature</Text>
                        </View>
                    </View>
                </View>
            </Page>

            {isQuotation ? null : (firstInvoice || data?.vehicles?.length <= 10) ?
                <Page size="A4" style={styles.page}>
                    <VehicleStatement isFullPage={false} vehicles={data?.vehicles ?? []} total={data?.amount ?? 0} />
                </Page> : null}
        </Document>
    );
};

export default TableWithSplitAndMerged;